import * as React from 'react';

import type {
  EnterpriseBadge_EnterpriseBadge,
  Specialization_Specialization,
} from '__generated__/graphql-types-do-not-use';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import type { ContentType, ProductType } from 'bundles/enterprise-admin-assigned-content/types';
import type { CreateMessageParams } from 'bundles/enterprise-admin-messages/types';
import { CallToActionId } from 'bundles/enterprise-admin-messages/types';
import { Button, TrackedButton } from 'bundles/enterprise-ui';
import type { EnterpriseNotification } from 'bundles/enterprise-ui/components/NotificationsContext';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';

import _t from 'i18n!nls/enterprise-admin-assigned-content';

export const buildAssignedLearnerEmailTemplate = (contentName = _t('content')) => ({
  subject: _t('Your assigned course in Coursera is waiting for you!'),
  initialCML: {
    typeName: 'cml' as const,
    definition: {
      dtdId: 'email/1',
      value: `<co-content><text>${[
        _t('Hi #{learner},').replace('#{learner}', '<ptag>LEARNER_NAME</ptag>'),
        _t(
          'You were recently assigned #{contentName} on Coursera, and we noticed you haven’t completed the content yet.',
          { contentName }
        ),
        _t(
          "We understand that life can get busy, but we want to make sure you don't miss out on the opportunity to learn and grow!"
        ),
        _t('Here are some tips to help you stay motivated and complete the content:'),
        _t('- Set a daily or weekly schedule to work on the content'),
        _t('- Break the content into smaller, more manageable chunks'),
        _t('- Reach out to your peers or instructors if you need help or have questions'),
        _t('We hope you jump back in soon!'),
      ].join('</text><text>')}</text></co-content>`,
    },
  },
});

export const getMessageParams = (
  learners: { userId: string; fullName: string }[],
  contentName: string
): CreateMessageParams => ({
  presetId: 'remind-assigned-learners',
  callToActionId: CallToActionId.CONTINUE_LEARNING,
  filters: {
    programs: [],
    enrollments: [],
    contracts: [],
    businessUnits: [],
    jobTitles: [],
    locations: [],
    managers: [],
  },
  users: learners.map((_) => ({
    fullName: _.fullName,
    userIdentifier: String(_.userId),
  })) as $TSFixMe,
  usersCount: learners.length,
  refetchUsersCount: false,
  emailTemplate: buildAssignedLearnerEmailTemplate(contentName),
});

export const buildSelectAllAssignedLearnersMessageParams = (contentName?: string) => ({
  params: {
    useNewUserFilter: true,
    presetId: 'remind-assigned-learners',
    callToActionId: CallToActionId.CONTINUE_LEARNING,
    filters: {
      programs: [],
      enrollments: [],
      contracts: [],
      businessUnits: [],
      jobTitles: [],
      locations: [],
      managers: [],
    },
    users: [],
    provideFilterContexts: [],
    usersCount: 0,
    refetchUsersCount: true,
    emailTemplate: buildAssignedLearnerEmailTemplate(contentName),
  },
});

export const getPostAssignmentNotifications = ({
  isAsync = false,
  preProcessingFailure,
  userNotInProgramFailures,
  contentNotEnrollableForUserFailures,
  userAlreadyAssignedFailures,
  contentNotInProgramFailures,
  namespace,
  content,
  programName,
  successfullyAssignedUserIds,
  numberOfUsersPreviewed,
  getLearnerName,
  onViewAssignments,
  onAddToCollection,
}: {
  isAsync?: boolean;
  preProcessingFailure?: 'CONTENT_NOT_IN_PROGRAM' | 'CSV_INCORRECT_FORMAT';
  userNotInProgramFailures: { userId: string }[];
  contentNotEnrollableForUserFailures: { userId: string }[];
  userAlreadyAssignedFailures: { userId: string }[];
  contentNotInProgramFailures: { userId: string }[];
  namespace: string;
  content: string;
  programName: string;
  programId: string;
  successfullyAssignedUserIds: string[];
  numberOfUsersPreviewed?: number;
  getLearnerName: (id: string) => string | undefined;
  onViewAssignments?: () => void;
  onAddToCollection: () => void;
}) => {
  const notifications: EnterpriseNotification[] = [];

  if (isAsync) {
    notifications.push({
      severity: 'information',
      title: _t('We’re processing your request to assign #{content} to #{count} learners', {
        content,
        count: numberOfUsersPreviewed,
      }),
      children: _t("You will receive an email notification once it's completed."),
      namespace,
      component: 'PageNotification',
    });
  }

  if (userNotInProgramFailures.length) {
    const learner = getLearnerName(userNotInProgramFailures[0].userId);
    notifications.push({
      severity: 'error',
      children: (
        <FormattedHTMLMessage
          message={
            userNotInProgramFailures.length === 1 && learner
              ? _t('{learner} is not in this program')
              : _t('{count} {count, plural, =1 {learner is} other {learners are}} not in this program')
          }
          learner={learner}
          count={userNotInProgramFailures.length}
        />
      ),
      namespace,
      component: 'PageNotification',
    });
  }
  if (contentNotEnrollableForUserFailures.length) {
    const learner = getLearnerName(contentNotEnrollableForUserFailures[0].userId);
    notifications.push({
      severity: 'error',
      children: (
        <FormattedHTMLMessage
          message={
            contentNotEnrollableForUserFailures.length === 1 && learner
              ? _t(
                  "<strong>{content}</strong> can't be assigned to {learner} as they are already enrolled in this content in another program."
                )
              : _t(
                  "<strong>{content}</strong> can't be assigned to {count} learners as they are already enrolled in this content in another program."
                )
          }
          content={content}
          learner={learner}
          count={contentNotEnrollableForUserFailures.length}
        />
      ),
      namespace,
      component: 'PageNotification',
    });
  }

  if (userAlreadyAssignedFailures.length) {
    const learner = getLearnerName(userAlreadyAssignedFailures[0].userId);
    notifications.push({
      severity: 'error' as const,
      children: (
        <FormattedHTMLMessage
          message={
            userAlreadyAssignedFailures.length === 1 && learner
              ? _t('<strong>{content}</strong> is already assigned to {learner}')
              : _t('<strong>{content}</strong> is already assigned to {count} learners')
          }
          content={content}
          learner={learner}
          count={userAlreadyAssignedFailures.length}
        />
      ),
      namespace,
      component: 'PageNotification' as const,
    });
  }

  if (contentNotInProgramFailures.length || preProcessingFailure === 'CONTENT_NOT_IN_PROGRAM') {
    notifications.push({
      severity: 'error' as const,
      children: (
        <FormattedHTMLMessage
          message={_t(
            '<strong>{content}</strong> isn’t currently available in {programName}. Please add the course to a collection first.'
          )}
          content={content}
          programName={programName}
        />
      ),
      inlineAction: (
        <TrackedButton
          trackingName="CONTENT_NOT_IN_PROGRAM_ADD_TO_COLLECTION"
          size="small"
          variant="ghost"
          onClick={onAddToCollection}
        >
          {_t('Add to collection')}
        </TrackedButton>
      ),
      namespace,
      component: 'PageNotification' as const,
    });
  }

  if (successfullyAssignedUserIds.length) {
    const learner = successfullyAssignedUserIds[0] ? getLearnerName(successfullyAssignedUserIds[0]) : '';
    const message =
      successfullyAssignedUserIds?.length === 1 && learner
        ? _t('<strong>{content}</strong> was successfully assigned to {learner}')
        : _t('<strong>{content}</strong> was successfully assigned to {count} more learners');

    notifications.push({
      severity: 'success',
      children: (
        <FormattedHTMLMessage
          message={message}
          content={content}
          learner={learner}
          count={successfullyAssignedUserIds.length}
        />
      ),
      namespace,
      component: 'PageNotification',
      inlineAction:
        namespace === 'ContentSearch' ? (
          <Button variant="ghost" size="small" onClick={onViewAssignments}>
            {_t('View')}
          </Button>
        ) : undefined,
    });
  }
  return notifications;
};

export const getPreAssignmentNotifications = ({
  isAsync = false,
  preProcessingFailure,
  namespace,
  content,
  programName,
  successfullyAssignedUserIds,
  numberOfUsersPreviewed,
  numberOfUsersNotInProgram,
  contentNotEnrollableForUserFailures,
  userAlreadyAssignedFailures,
  onViewAssignments,
  onAddToCollection,
}: {
  isAsync?: boolean;
  preProcessingFailure?: 'CONTENT_NOT_IN_PROGRAM' | 'CSV_INCORRECT_FORMAT';
  namespace: string;
  content: string;
  programName: string;
  successfullyAssignedUserIds: string[];
  numberOfUsersPreviewed?: number;
  numberOfUsersNotInProgram: number;
  emailsNotInProgramCsvLink: string;
  contentNotEnrollableForUserFailures: { email?: string | null }[];
  userAlreadyAssignedFailures: { email?: string | null }[];
  onViewAssignments?: () => void;
  onAddToCollection: () => void;
}) => {
  const notifications: EnterpriseNotification[] = [];

  if (preProcessingFailure === 'CSV_INCORRECT_FORMAT') {
    notifications.push({
      severity: 'error',
      title: _t('Your CSV file doesn’t match our required format'),
      children: _t('Column headers must match (Email)'),
      namespace,
      dismissable: false,
    });
  } else if (isAsync) {
    notifications.push({
      severity: 'success',
      children: numberOfUsersPreviewed ? (
        <FormattedHTMLMessage
          message={_t('<strong>{count}</strong> {count, plural, =1 {email} other {emails}} found')}
          count={numberOfUsersPreviewed}
        />
      ) : (
        _t('Assigning to all emails found in CSV import')
      ),
      namespace,
      dismissable: false,
    });
  }
  if (preProcessingFailure === 'CONTENT_NOT_IN_PROGRAM') {
    notifications.push({
      severity: 'error' as const,
      children: (
        <FormattedHTMLMessage
          message={_t(
            '<strong>{content}</strong> isn’t currently available in {programName}. Please add the course to a collection first.'
          )}
          content={content}
          programName={programName}
        />
      ),
      inlineAction: (
        <TrackedButton
          trackingName="CONTENT_NOT_IN_PROGRAM_ADD_TO_COLLECTION"
          size="small"
          variant="ghost"
          onClick={onAddToCollection}
        >
          {_t('Add to collection')}
        </TrackedButton>
      ),
      namespace,
      dismissable: false,
    });
  }
  if (numberOfUsersNotInProgram) {
    notifications.push({
      severity: 'warning',
      children: (
        <FormattedHTMLMessage
          message={_t(
            '<strong>{count}</strong> {count, plural, =1 {email is} other {emails are}} not currently tied to any learners in <strong>{program}</strong>'
          )}
          count={numberOfUsersNotInProgram}
          program={programName}
        />
      ),
      namespace,
      dismissable: false,
    });
  }
  if (contentNotEnrollableForUserFailures.length) {
    const learner = contentNotEnrollableForUserFailures[0].email;
    notifications.push({
      severity: 'error',
      children: (
        <FormattedHTMLMessage
          message={
            contentNotEnrollableForUserFailures.length === 1 && learner
              ? _t(
                  "<strong>{content}</strong> can't be assigned to {learner} as they are already enrolled in this content in another program."
                )
              : _t(
                  "<strong>{content}</strong> can't be assigned to {count} learners as they are already enrolled in this content in another program."
                )
          }
          content={content}
          learner={learner}
          count={contentNotEnrollableForUserFailures.length}
        />
      ),
      namespace,
    });
  }

  if (userAlreadyAssignedFailures.length) {
    const learner = userAlreadyAssignedFailures[0].email;
    notifications.push({
      severity: 'error' as const,
      children: (
        <FormattedHTMLMessage
          message={
            userAlreadyAssignedFailures.length === 1 && learner
              ? _t('<strong>{content}</strong> is already assigned to {learner}')
              : _t('<strong>{content}</strong> is already assigned to {count} learners')
          }
          content={content}
          learner={learner}
          count={userAlreadyAssignedFailures.length}
        />
      ),
      namespace,
    });
  }
  if (successfullyAssignedUserIds.length) {
    const message = _t(
      '<strong>{count}</strong> {count, plural, =1 {email} other {emails}} match existing learners in <strong>{program}</strong>'
    );
    notifications.push({
      severity: 'success',
      children: (
        <FormattedHTMLMessage message={message} program={programName} count={successfullyAssignedUserIds.length} />
      ),
      namespace,
      inlineAction:
        namespace === 'ContentSearch' ? (
          <Button variant="ghost" size="small" onClick={onViewAssignments}>
            {_t('View')}
          </Button>
        ) : undefined,
      dismissable: false,
    });
  }
  if (isAsync === false && successfullyAssignedUserIds.length === 0) {
    notifications.push({
      severity: 'error',
      children: _t('No valid emails found'),
      namespace,
      dismissable: false,
    });
  }
  return notifications;
};

export const getAssignmentUrlForSearchHit = (orgSlug: string, programSlug: string, hit: SearchProductHit) => {
  // the hit does not contain the slug, but the url does
  // for courses the url looks like /learn/programming-in-python
  // for guided projects the url looks like /projects/power-bi-desktop
  // for specializations the url looks like /specializations/learnquest-java-testing
  // assuming the product slug is the last part of the url
  const productSlug = hit.url?.split('/').pop();
  const productType =
    {
      COURSE: 'course',
      GUIDED_PROJECT: 'project',
      PROJECT: 'project',
      SPECIALIZATION: 'specialization',
      PROFESSIONAL_CERTIFICATE: 'professional-certificate',
    }[hit.productType as string] ?? 'unsupported-product-type';
  return `/o/${orgSlug}/content-assignment/${programSlug}/${productType}/${productSlug}`;
};

export const getContentType = (productType: ProductType): ContentType => {
  return (
    {
      course: 'COURSE',
      project: 'COURSE',
      specialization: 'SPECIALIZATION',
      'professional-certificate': 'SPECIALIZATION',
      'learning-path': 'LEARNING_PATH',
    } as const
  )[productType];
};

type Product =
  | {
      __typename?: 'Course_Course' | undefined;
      courseTypeMetadata: { __typename?: 'Course_CourseTypeMetadataWithVersion' | undefined } & {
        metadata:
          | { __typename: 'Course_ProjectMetadata' }
          | { __typename: 'Course_RhymeProjectMetadata' }
          | { __typename: 'Course_ShortFormContentMetadata' }
          | { __typename: 'Course_StandardCourseMetadata' };
      };
    }
  | ({ __typename?: 'Specialization_Specialization' | undefined } & Pick<
      Specialization_Specialization,
      'productVariant'
    >)
  | ({ __typename?: 'EnterpriseBadge_EnterpriseBadge' } & Pick<
      EnterpriseBadge_EnterpriseBadge,
      'description' | 'id' | 'imageUrl' | 'learningObjectives' | 'name' | 'slug'
    >)
  | undefined;

export const getProductType = (product: Product): ProductType => {
  if (product?.__typename === 'Course_Course') {
    const type = product.courseTypeMetadata.metadata.__typename;
    const productType = (
      {
        Course_ProjectMetadata: 'project',
        Course_RhymeProjectMetadata: 'project',
        Course_ShortFormContentMetadata: 'course',
        Course_StandardCourseMetadata: 'course',
      } as const
    )[type];
    return productType;
  }
  if (product?.__typename === 'Specialization_Specialization') {
    const type = product.productVariant;
    const productType = (
      {
        PROFESSIONAL_CERTIFICATE: 'professional-certificate',
        PROFESSIONAL_CERTIFICATE_S12N: 'professional-certificate',
        SPECIALIZATION: 'specialization',
        EXTERNAL_CERTIFICATE_S12N: 'specialization',
        GOOGLE_CERTIFICATE_S12N: 'specialization',
        NORMAL_S12N: 'specialization',
      } as const
    )[type];
    return productType;
  }
  if (product?.__typename === 'EnterpriseBadge_EnterpriseBadge') {
    return 'learning-path';
  }
  return 'course';
};

export const getByProductType = (product: Product, values: { [key in ProductType]: string }): string => {
  return values[getProductType(product)];
};

export const getProductImage = (product?: {
  __typename?: 'Course_Course' | 'Specialization_Specialization' | 'EnterpriseBadge_EnterpriseBadge';
  promoPhotoUrl?: string | null;
  cardImageUrl?: string | null;
  imageUrl?: string | null;
}): string => {
  return (
    (product?.__typename === 'Course_Course' && product.promoPhotoUrl) ||
    (product?.__typename === 'Specialization_Specialization' && product.cardImageUrl) ||
    (product?.__typename === 'EnterpriseBadge_EnterpriseBadge' && product.imageUrl) ||
    ''
  );
};

export const getCancelAssignmentUrl = (thirdPartySlug: string, programSlug: string, ref: string) => {
  const assignedContent = {
    pathname: `/o/${thirdPartySlug}/admin/programs/${programSlug}/curriculum`,
    search: '?tab=ASSIGNED_CONTENT',
  };
  return (
    {
      'original-flow': assignedContent,
      'assign-more-from-assigned-to-learners': assignedContent,
      'insights-page': assignedContent,
      'program-catalog': `/o/${thirdPartySlug}/admin/programs/${programSlug}/curriculum`,
      'manage-learners': `/o/${thirdPartySlug}/admin/programs/${programSlug}/manage-learners`,
    }[String(ref)] ?? `/o/${thirdPartySlug}/admin`
  );
};

export const getContentId = (product?: {
  __typename?: 'Course_Course' | 'Specialization_Specialization' | 'EnterpriseBadge_EnterpriseBadge';
  baseSpecialization?: { id: string };
  id: string;
}): string => {
  if (product?.__typename === 'Course_Course' || product?.__typename === 'EnterpriseBadge_EnterpriseBadge') {
    return product?.id;
  } else if (product?.__typename === 'Specialization_Specialization') {
    return product?.baseSpecialization?.id ?? '';
  }
  return '';
};
